import React from "react";
import { FormLabel } from "components/form";
import { FormField, FormItem, FormMessage } from "ui/components/form";
import ButtonWithLoading from "@/components/button-with-loading";
import { Form } from "react-crud-form";
import { Input } from "ui/components/input";
import { PermissionControlledInput } from "permissions/components/input";
import HeaderPortal from "components/portal/header-portal";
import { meetingSchema } from "./schema";
import { useHotkeys } from "react-hotkeys-hook";
import { useMeetingForm } from "./useMeetingForm";
import { Button } from "ui/components/button";
import { X, Plus } from "lucide-react";
import { useFieldArray } from "react-hook-form";
import { DateTimePicker } from "components/datetime-picker";

const MeetingForm = ({ meetingId }) => {

    const { form, requiredFields, handleCreate, handleRead, handleUpdate } =
        useMeetingForm({ meetingId });

    const { fields: participantFields, append, remove } = useFieldArray({
        control: form.control,
        name: "participants",
    });

    const addParticipant = () => {
        append({ email: "", _isNew: true });
    };

    return (
        <Form
            form={form}
            create={handleCreate}
            read={handleRead}
            update={handleUpdate}
            itemId={meetingId || "+"}
            schema={meetingSchema}
        >
            {({
                isCreating,
                createItem,
                isNewItem,
                isUpdating,
                updateItem,
                isDirty,
            }) => {
                useHotkeys(
                    "ctrl+s, cmd+s",
                    (event) => {
                        event.preventDefault();
                        if (!isCreating) {
                            createItem();
                        }
                    },
                    { preventDefault: true }
                );

                useHotkeys(
                    "ctrl+shift+s, cmd+shift+s",
                    (event) => {
                        event.preventDefault();
                        if (!isCreating) {
                            createItem();
                        }
                    },
                    { preventDefault: true }
                );
                return (
                    <>
                        <div className="space-y-6">
                            {form.error && (
                                <div className="text-red-600 bg-red-50 p-2 rounded">
                                    {form.error.message || form.error}
                                </div>
                            )}
                            {/* Title */}
                            <FormField
                                control={form.control}
                                name="title"
                                render={({ field }) => {
                                    return (
                                        <FormItem>
                                            <PermissionControlledInput
                                                rule={isNewItem ? "create:*" : "update:*"}
                                                collection="calendar_events"
                                                type="form"
                                                field={field.name}
                                            >
                                                <FormLabel
                                                    required={requiredFields.includes(field.name)}
                                                >
                                                    Title
                                                </FormLabel>

                                                <Input {...field} />
                                            </PermissionControlledInput>
                                            <FormMessage />
                                        </FormItem>
                                    );
                                }}
                            />

                            {/* Start Date */}
                            <FormField
                                control={form.control}
                                name="starts_at"
                                render={({ field }) => (
                                    <FormItem>
                                        <PermissionControlledInput
                                            rule={isNewItem ? "create:*" : "update:*"}
                                            collection="calendar_events"
                                            field={field.name}
                                            type="form"
                                            showWhenDisabled={false}
                                        >
                                            <FormLabel required={requiredFields.includes(field.name)}>
                                                Start Date & Time
                                            </FormLabel>
                                            {/* <Input
                                                type="datetime-local"
                                                value={(field.value)}
                                                onChange={(e) => {
                                                    const utcValue = e.target.value ? new Date(e.target.value).toISOString() : null;
                                                    field.onChange(utcValue);
                                                }}
                                            /> */}
                                            <DateTimePicker
                                                value={field.value}
                                                onChange={field.onChange}
                                                disabled={false}
                                            />
                                        </PermissionControlledInput>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            {/* End Date */}
                            <FormField
                                control={form.control}
                                name="ends_at"
                                render={({ field }) => (
                                    <FormItem>
                                        <PermissionControlledInput
                                            rule={isNewItem ? "create:*" : "update:*"}
                                            collection="calendar_events"
                                            field={field.name}
                                            type="form"
                                            showWhenDisabled={false}
                                        >
                                            <FormLabel required={requiredFields.includes(field.name)}>
                                                End Date & Time
                                            </FormLabel>
                                            <DateTimePicker
                                                value={field.value}
                                                onChange={field.onChange}
                                                disabled={false}
                                            />
                                        </PermissionControlledInput>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            {/* Participants Section */}
                            <div className="border-t pt-4">
                                <div className="flex items-center justify-between mb-4">
                                    <FormLabel>Participants</FormLabel>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={addParticipant}
                                        className="gap-2"
                                    >
                                        <Plus className="size-4" />
                                        Add Participant
                                    </Button>
                                </div>

                                <div className="space-y-3">
                                    {participantFields.length === 0 ? (
                                        <p className="text-sm text-gray-500">
                                            No participants added yet
                                        </p>
                                    ) : (
                                        participantFields.map((field, index) => (
                                            <div
                                                key={field.id}
                                                className="flex items-end gap-2"
                                            >
                                                <FormField
                                                    control={form.control}
                                                    name={`participants.${index}.email`}
                                                    render={({ field: emailField }) => (
                                                        <FormItem className="flex-1">
                                                            <Input
                                                                {...emailField}
                                                                placeholder="participant@example.com"
                                                                type="email"
                                                            />
                                                            <FormMessage />
                                                        </FormItem>
                                                    )}
                                                />
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => remove(index)}
                                                    className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                                >
                                                    <X className="size-4" />
                                                </Button>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>

                            {/* Portal buttons */}
                            <HeaderPortal>
                                {isNewItem ? (
                                    <ButtonWithLoading
                                        variant="default"
                                        isLoading={isCreating}
                                        onClick={createItem}
                                    >
                                        Save
                                    </ButtonWithLoading>
                                ) : (
                                    <ButtonWithLoading
                                        variant="default"
                                        isLoading={isUpdating}
                                        disabled={!isDirty}
                                        onClick={updateItem}
                                    >
                                        Update
                                    </ButtonWithLoading>
                                )}
                            </HeaderPortal>
                        </div>
                    </>
                );
            }}
        </Form>
    );
};

export default MeetingForm;
